import React, { useState } from 'react';
import {
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  BeakerIcon,
  AdjustmentsHorizontalIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline';
import { DailyNutritionalSummary, PARROT_NUTRITIONAL_REQUIREMENTS } from '../api/nutrition';

interface NutritionalAnalysisPanelProps {
  nutritionalSummary: DailyNutritionalSummary;
  totalQuantity: number;
  className?: string;
}

interface NutrientDisplayProps {
  name: string;
  current: number;
  target: { min: number; max: number; optimal: number };
  unit: string;
  category: 'macronutrient' | 'mineral' | 'vitamin';
}

const NutrientDisplay: React.FC<NutrientDisplayProps> = ({
  name,
  current,
  target,
  unit,
  category
}) => {
  const getStatusColor = () => {
    if (current >= target.min && current <= target.max) {
      return 'text-green-600 bg-green-50';
    } else if (current < target.min) {
      return 'text-red-600 bg-red-50';
    } else {
      return 'text-yellow-600 bg-yellow-50';
    }
  };

  const getStatusIcon = () => {
    if (current >= target.min && current <= target.max) {
      return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
    } else if (current < target.min) {
      return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
    } else {
      return <ExclamationTriangleIcon className="h-4 w-4 text-yellow-500" />;
    }
  };

  const getProgressWidth = () => {
    const range = target.max - target.min;
    const position = Math.max(0, Math.min(100, ((current - target.min) / range) * 100));
    return `${position}%`;
  };

  const getProgressColor = () => {
    if (current >= target.min && current <= target.max) {
      return 'bg-green-500';
    } else if (current < target.min) {
      return 'bg-red-500';
    } else {
      return 'bg-yellow-500';
    }
  };

  return (
    <div className="p-3 border rounded-lg bg-white hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          {getStatusIcon()}
          <span className="text-sm font-medium text-slate-900">{name}</span>
        </div>
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor()}`}>
          {current.toFixed(2)} {unit}
        </span>
      </div>
      
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-slate-500">
          <span>Min: {target.min}</span>
          <span>Optimal: {target.optimal}</span>
          <span>Max: {target.max}</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-500 ${getProgressColor()}`}
            style={{ width: getProgressWidth() }}
          ></div>
        </div>
      </div>
    </div>
  );
};

export default function NutritionalAnalysisPanel({
  nutritionalSummary,
  totalQuantity,
  className = ''
}: NutritionalAnalysisPanelProps) {
  const [activeTab, setActiveTab] = useState<'macronutrients' | 'minerals' | 'vitamins' | 'overview'>('overview');
  const [showDeficienciesOnly, setShowDeficienciesOnly] = useState(false);

  // Calculate per-kg values for comparison with requirements
  const calculatePerKgValue = (totalValue: number | undefined): number => {
    if (!totalValue || totalQuantity === 0) return 0;
    return totalValue / totalQuantity;
  };

  const macronutrients = [
    {
      name: 'Protein',
      current: (nutritionalSummary.proteinPercentage / 100) * 1000,
      target: PARROT_NUTRITIONAL_REQUIREMENTS.protein,
      unit: 'g/kg'
    },
    {
      name: 'Fat',
      current: (nutritionalSummary.fatPercentage / 100) * 1000,
      target: PARROT_NUTRITIONAL_REQUIREMENTS.fat,
      unit: 'g/kg'
    },
    {
      name: 'Fiber',
      current: (nutritionalSummary.fiberPercentage / 100) * 1000,
      target: PARROT_NUTRITIONAL_REQUIREMENTS.fiber,
      unit: 'g/kg'
    },
    {
      name: 'Moisture',
      current: (nutritionalSummary.moisturePercentage / 100) * 1000,
      target: PARROT_NUTRITIONAL_REQUIREMENTS.moisture,
      unit: 'g/kg'
    }
  ];

  const minerals = [
    {
      name: 'Calcium',
      current: calculatePerKgValue(nutritionalSummary.calciumMg) / 1000, // mg/kg → g/kg
      target: PARROT_NUTRITIONAL_REQUIREMENTS.calcium,
      unit: 'g/kg'
    },
    {
      name: 'Phosphorus',
      current: calculatePerKgValue(nutritionalSummary.phosphorusMg) / 1000, // mg/kg → g/kg
      target: PARROT_NUTRITIONAL_REQUIREMENTS.phosphorus,
      unit: 'g/kg'
    },
    {
      name: 'Magnesium',
      current: calculatePerKgValue(nutritionalSummary.magnesiumMg) / 1000, // mg/kg → g/kg
      target: PARROT_NUTRITIONAL_REQUIREMENTS.magnesium,
      unit: 'g/kg'
    },
    {
      name: 'Potassium',
      current: calculatePerKgValue(nutritionalSummary.potassiumMg) / 1000, // mg/kg → g/kg
      target: PARROT_NUTRITIONAL_REQUIREMENTS.potassium,
      unit: 'g/kg'
    },
    {
      name: 'Sodium',
      current: calculatePerKgValue(nutritionalSummary.sodiumMg) / 1000, // mg/kg → g/kg
      target: PARROT_NUTRITIONAL_REQUIREMENTS.sodium,
      unit: 'g/kg'
    },
    {
      name: 'Iron',
      current: calculatePerKgValue(nutritionalSummary.ironMg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.iron,
      unit: 'mg/kg'
    },
    {
      name: 'Zinc',
      current: calculatePerKgValue(nutritionalSummary.zincMg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.zinc,
      unit: 'mg/kg'
    },
    {
      name: 'Manganese',
      current: calculatePerKgValue(nutritionalSummary.manganeseMg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.manganese,
      unit: 'mg/kg'
    },
    {
      name: 'Copper',
      current: calculatePerKgValue(nutritionalSummary.copperMg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.copper,
      unit: 'mg/kg'
    },
    {
      name: 'Selenium',
      current: calculatePerKgValue(nutritionalSummary.seleniumMg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.selenium,
      unit: 'mg/kg'
    }
  ];

  const vitamins = [
    {
      name: 'Vitamin A',
      current: calculatePerKgValue(nutritionalSummary.vitaminA_IU), // IU/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.vitaminA,
      unit: 'IU/kg'
    },
    {
      name: 'Vitamin D3',
      current: calculatePerKgValue(nutritionalSummary.vitaminD3_IU), // IU/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.vitaminD3,
      unit: 'IU/kg'
    },
    {
      name: 'Vitamin E',
      current: calculatePerKgValue(nutritionalSummary.vitaminE_IU), // IU/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.vitaminE,
      unit: 'IU/kg'
    },
    {
      name: 'Vitamin K',
      current: calculatePerKgValue(nutritionalSummary.vitaminK_mg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.vitaminK,
      unit: 'mg/kg'
    },
    {
      name: 'Thiamine (B1)',
      current: calculatePerKgValue(nutritionalSummary.thiamine_mg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.thiamine,
      unit: 'mg/kg'
    },
    {
      name: 'Riboflavin (B2)',
      current: calculatePerKgValue(nutritionalSummary.riboflavin_mg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.riboflavin,
      unit: 'mg/kg'
    },
    {
      name: 'Niacin (B3)',
      current: calculatePerKgValue(nutritionalSummary.niacin_mg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.niacin,
      unit: 'mg/kg'
    },
    {
      name: 'Pantothenic Acid (B5)',
      current: calculatePerKgValue(nutritionalSummary.pantothenicAcid_mg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.pantothenicAcid,
      unit: 'mg/kg'
    },
    {
      name: 'Pyridoxine (B6)',
      current: calculatePerKgValue(nutritionalSummary.pyridoxine_mg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.pyridoxine,
      unit: 'mg/kg'
    },
    {
      name: 'Biotin',
      current: calculatePerKgValue(nutritionalSummary.biotin_mg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.biotin,
      unit: 'mg/kg'
    },
    {
      name: 'Folic Acid',
      current: calculatePerKgValue(nutritionalSummary.folicAcid_mg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.folicAcid,
      unit: 'mg/kg'
    },
    {
      name: 'Vitamin B12',
      current: calculatePerKgValue(nutritionalSummary.vitaminB12_mg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.vitaminB12,
      unit: 'mg/kg'
    },
    {
      name: 'Choline',
      current: calculatePerKgValue(nutritionalSummary.choline_mg), // mg/kg (already correct)
      target: PARROT_NUTRITIONAL_REQUIREMENTS.choline,
      unit: 'mg/kg'
    }
  ];

  const filterNutrients = (nutrients: any[]) => {
    if (!showDeficienciesOnly) return nutrients;
    return nutrients.filter(nutrient => nutrient.current < nutrient.target.min);
  };

  const getAdequacyColor = (score: number) => {
    if (score >= 85) return 'text-green-600 bg-green-100';
    if (score >= 70) return 'text-cyan-600 bg-blue-100';
    if (score >= 60) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const tabs = [
    { id: 'overview', name: 'Overview', icon: ChartBarIcon },
    { id: 'macronutrients', name: 'Macronutrients', icon: BeakerIcon },
    { id: 'minerals', name: 'Minerals', icon: AdjustmentsHorizontalIcon },
    { id: 'vitamins', name: 'Vitamins', icon: InformationCircleIcon }
  ];

  return (
    <div className={`bg-white rounded-lg shadow-sm border ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-slate-900">Nutritional Analysis</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowDeficienciesOnly(!showDeficienciesOnly)}
              className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-md ${
                showDeficienciesOnly
                  ? 'bg-red-100 text-red-700'
                  : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
              }`}
            >
              {showDeficienciesOnly ? (
                <EyeSlashIcon className="h-4 w-4 mr-1" />
              ) : (
                <EyeIcon className="h-4 w-4 mr-1" />
              )}
              {showDeficienciesOnly ? 'Show All' : 'Deficiencies Only'}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8 px-6" aria-label="Tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`${
                activeTab === tab.id
                  ? 'border-cyan-500 text-cyan-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2`}
            >
              <tab.icon className="h-4 w-4" />
              <span>{tab.name}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Adequacy Scores */}
            {nutritionalSummary.adequacyScores && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center p-4 bg-slate-50/80 rounded-lg">
                  <div className={`text-2xl font-bold ${getAdequacyColor(nutritionalSummary.adequacyScores.macronutrients)}`}>
                    {nutritionalSummary.adequacyScores.macronutrients.toFixed(0)}%
                  </div>
                  <div className="text-sm text-slate-600">Macronutrients</div>
                </div>
                <div className="text-center p-4 bg-slate-50/80 rounded-lg">
                  <div className={`text-2xl font-bold ${getAdequacyColor(nutritionalSummary.adequacyScores.minerals)}`}>
                    {nutritionalSummary.adequacyScores.minerals.toFixed(0)}%
                  </div>
                  <div className="text-sm text-slate-600">Minerals</div>
                </div>
                <div className="text-center p-4 bg-slate-50/80 rounded-lg">
                  <div className={`text-2xl font-bold ${getAdequacyColor(nutritionalSummary.adequacyScores.vitamins)}`}>
                    {nutritionalSummary.adequacyScores.vitamins.toFixed(0)}%
                  </div>
                  <div className="text-sm text-slate-600">Vitamins</div>
                </div>
                <div className="text-center p-4 bg-slate-50/80 rounded-lg">
                  <div className={`text-2xl font-bold ${getAdequacyColor(nutritionalSummary.adequacyScores.overall)}`}>
                    {nutritionalSummary.adequacyScores.overall.toFixed(0)}%
                  </div>
                  <div className="text-sm text-slate-600">Overall</div>
                </div>
              </div>
            )}

            {/* Nutritional Alerts */}
            {nutritionalSummary.nutritionalAlerts && nutritionalSummary.nutritionalAlerts.length > 0 && (
              <div className="space-y-3">
                <h4 className="font-medium text-slate-900">Nutritional Alerts</h4>
                <div className="space-y-2">
                  {nutritionalSummary.nutritionalAlerts.slice(0, 5).map((alert, index) => (
                    <div
                      key={index}
                      className={`p-3 rounded-lg border ${
                        alert.severity === 'high'
                          ? 'bg-red-50 border-red-200'
                          : alert.severity === 'medium'
                          ? 'bg-yellow-50 border-yellow-200'
                          : 'bg-cyan-50 border-blue-200'
                      }`}
                    >
                      <div className="flex items-start space-x-3">
                        <ExclamationTriangleIcon 
                          className={`h-5 w-5 mt-0.5 ${
                            alert.severity === 'high'
                              ? 'text-red-500'
                              : alert.severity === 'medium'
                              ? 'text-yellow-500'
                              : 'text-blue-500'
                          }`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h5 className={`font-medium ${
                              alert.severity === 'high'
                                ? 'text-red-800'
                                : alert.severity === 'medium'
                                ? 'text-yellow-800'
                                : 'text-cyan-800'
                            }`}>
                              {alert.nutrient} {alert.type.charAt(0).toUpperCase() + alert.type.slice(1)}
                            </h5>
                            <span className={`text-sm ${
                              alert.severity === 'high'
                                ? 'text-red-600'
                                : alert.severity === 'medium'
                                ? 'text-yellow-600'
                                : 'text-cyan-600'
                            }`}>
                              {alert.currentValue.toFixed(2)} {alert.unit}
                            </span>
                          </div>
                          <p className={`text-sm mt-1 ${
                            alert.severity === 'high'
                              ? 'text-red-700'
                              : alert.severity === 'medium'
                              ? 'text-yellow-700'
                              : 'text-cyan-700'
                          }`}>
                            Target: {alert.targetRange.min}-{alert.targetRange.max} {alert.unit}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                  {nutritionalSummary.nutritionalAlerts.length > 5 && (
                    <p className="text-sm text-slate-500 text-center">
                      +{nutritionalSummary.nutritionalAlerts.length - 5} more alerts
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'macronutrients' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filterNutrients(macronutrients).map((nutrient) => (
              <NutrientDisplay
                key={nutrient.name}
                name={nutrient.name}
                current={nutrient.current}
                target={nutrient.target}
                unit={nutrient.unit}
                category="macronutrient"
              />
            ))}
          </div>
        )}

        {activeTab === 'minerals' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filterNutrients(minerals).map((nutrient) => (
              <NutrientDisplay
                key={nutrient.name}
                name={nutrient.name}
                current={nutrient.current}
                target={nutrient.target}
                unit={nutrient.unit}
                category="mineral"
              />
            ))}
          </div>
        )}

        {activeTab === 'vitamins' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filterNutrients(vitamins).map((nutrient) => (
              <NutrientDisplay
                key={nutrient.name}
                name={nutrient.name}
                current={nutrient.current}
                target={nutrient.target}
                unit={nutrient.unit}
                category="vitamin"
              />
            ))}
          </div>
        )}

        {/* Empty state for filtered view */}
        {showDeficienciesOnly && (
          (activeTab === 'macronutrients' && filterNutrients(macronutrients).length === 0) ||
          (activeTab === 'minerals' && filterNutrients(minerals).length === 0) ||
          (activeTab === 'vitamins' && filterNutrients(vitamins).length === 0)
        ) && (
          <div className="text-center py-8">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-green-500" />
            <h3 className="mt-2 text-sm font-medium text-slate-900">No Deficiencies Found</h3>
            <p className="mt-1 text-sm text-slate-500">
              All nutrients in this category are within acceptable ranges.
            </p>
          </div>
        )}
      </div>
    </div>
  );
} 